import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PostListItem, PublicUser, Role, UserListResponse } from '../types';
import { api } from './client';
import { qk } from './keys';

export interface UsersListParams {
  email?: string;
  role?: Role;
  /** 'true' = only accounts with a pending deletion request. */
  deletionRequested?: 'true';
  page?: number;
  pageSize?: number;
}

export interface UserBookmarksResponse {
  items: PostListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface UserReactionItem {
  post: PostListItem;
  kind: 'like' | 'dislike';
  reactedAt: string;
}

export interface UserReactionsResponse {
  items: UserReactionItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface UserPushSub {
  id: string;
  platform: 'web' | 'ios' | 'android';
  locale: string | null;
  lastSeenAt: string;
  createdAt: string;
}

export interface UserPushSubsResponse {
  items: UserPushSub[];
}

export function useUsers(params: UsersListParams, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.users(params),
    enabled: opts?.enabled ?? true,
    queryFn: () => api.get<UserListResponse>('/admin/users', params),
  });
}

export function useUser(id: string | null) {
  return useQuery({
    queryKey: qk.user(id ?? ''),
    enabled: !!id,
    queryFn: () => api.get<PublicUser>(`/admin/users/${id}`),
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; role: Role }) =>
      api.post<PublicUser>(`/admin/users/${args.id}/role`, { role: args.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUserBookmarks(id: string | null, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['user-bookmarks', id, page, pageSize],
    enabled: !!id,
    queryFn: () =>
      api.get<UserBookmarksResponse>(`/admin/users/${id}/bookmarks`, { page, pageSize }),
  });
}

export function useUserReactions(id: string | null, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['user-reactions', id, page, pageSize],
    enabled: !!id,
    queryFn: () =>
      api.get<UserReactionsResponse>(`/admin/users/${id}/reactions`, { page, pageSize }),
  });
}

export function useUserPushSubs(id: string | null) {
  return useQuery({
    queryKey: ['user-push-subs', id],
    enabled: !!id,
    queryFn: () => api.get<UserPushSubsResponse>(`/admin/users/${id}/push-subscriptions`),
  });
}

export function useBulkDeleteUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number }>('/admin/users/bulk-delete', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/** Withdraw a staff account's deletion request (it can sign in again). */
export function useCancelDeletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PublicUser>(`/admin/users/${id}/cancel-deletion`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: qk.user(id) });
    },
  });
}
